import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from "@material-ui/core";

import {
  Assessment,
  DateRange,
  Search
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
  },
  filterSelect: {
    minWidth: 180,
  },
  summaryGrid: {
    marginBottom: theme.spacing(2),
  },
  summaryPaper: {
    padding: theme.spacing(2),
  },
  summaryTitle: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    margin: 0,
  },
  summaryValue: {
    color: theme.palette.primary.main,
    fontSize: 28,
    fontWeight: 600,
    margin: 0,
  },
  sectionTitle: {
    color: theme.palette.primary.main,
    fontSize: 18,
    fontWeight: 600,
    margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px`,
  },
  reportTable: {
    tableLayout: "fixed",
  },
  quantityColumn: {
    width: 160,
  },
}));

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getFirstDayOfMonth = () => {
  const date = new Date();
  return formatDate(new Date(date.getFullYear(), date.getMonth(), 1));
};

const getToday = () => formatDate(new Date());

const AttendanceReports = () => {
  const classes = useStyles();
  const { findAll: findAllQueues } = useQueues();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedQueueId, setSelectedQueueId] = useState("");
  const [report, setReport] = useState({
    attendants: [],
    sectors: [],
    total: 0
  });

  const fetchReport = async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/reports/attendances", {
        params: {
          startDate,
          endDate,
          userId: selectedUserId || undefined,
          queueId: selectedQueueId || undefined
        },
      });

      setReport(data);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [queueList, usersResponse] = await Promise.all([
          findAllQueues(),
          api.get("/users/report-select")
        ]);

        setQueues(queueList);
        setUsers(usersResponse.data.users);
      } catch (err) {
        toastError(err);
      }
    };

    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("attendanceReports.title")}</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filters}>
            <TextField
              label={i18n.t("attendanceReports.filters.startDate")}
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRange color="secondary" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={i18n.t("attendanceReports.filters.endDate")}
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRange color="secondary" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl
              variant="outlined"
              margin="dense"
              className={classes.filterSelect}
            >
              <InputLabel>{i18n.t("attendanceReports.filters.queue")}</InputLabel>
              <Select
                value={selectedQueueId}
                onChange={(event) => setSelectedQueueId(event.target.value)}
                label={i18n.t("attendanceReports.filters.queue")}
              >
                <MenuItem value="">{i18n.t("attendanceReports.filters.allQueues")}</MenuItem>
                {queues.map((queue) => (
                  <MenuItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              margin="dense"
              className={classes.filterSelect}
            >
              <InputLabel>{i18n.t("attendanceReports.filters.attendant")}</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
                label={i18n.t("attendanceReports.filters.attendant")}
              >
                <MenuItem value="">{i18n.t("attendanceReports.filters.allAttendants")}</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchReport}
              disabled={loading}
            >
              <Search />
            </Button>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={2} className={classes.summaryGrid}>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.summaryPaper}>
              <p className={classes.summaryTitle}>
                {i18n.t("attendanceReports.summary.total")}
              </p>
              <p className={classes.summaryValue}>{report.total}</p>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.summaryPaper}>
              <p className={classes.summaryTitle}>
                {i18n.t("attendanceReports.summary.attendants")}
              </p>
              <p className={classes.summaryValue}>{report.attendants.length}</p>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" className={classes.summaryPaper}>
              <p className={classes.summaryTitle}>
                {i18n.t("attendanceReports.summary.sectors")}
              </p>
              <p className={classes.summaryValue}>{report.sectors.length}</p>
            </Paper>
          </Grid>
        </Grid>

        <div className={classes.sectionTitle}>
          <Assessment fontSize="small" /> {i18n.t("attendanceReports.byAttendant")}
        </div>
        <Table size="small" className={classes.reportTable}>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("attendanceReports.table.attendant")}</TableCell>
              <TableCell align="center" className={classes.quantityColumn}>
                {i18n.t("attendanceReports.table.total")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.attendants.map((attendant) => (
              <TableRow key={attendant.userId || "without-user"}>
                <TableCell>{attendant.userName}</TableCell>
                <TableCell align="center" className={classes.quantityColumn}>
                  {attendant.total}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={2} />}
          </TableBody>
        </Table>

        <div className={classes.sectionTitle}>
          <Assessment fontSize="small" /> {i18n.t("attendanceReports.bySector")}
        </div>
        <Table size="small" className={classes.reportTable}>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("attendanceReports.table.sector")}</TableCell>
              <TableCell align="center" className={classes.quantityColumn}>
                {i18n.t("attendanceReports.table.total")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.sectors.map((sector) => (
              <TableRow key={sector.queueId || "without-queue"}>
                <TableCell>{sector.queueName}</TableCell>
                <TableCell align="center" className={classes.quantityColumn}>
                  {sector.total}
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={2} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default AttendanceReports;
